import { useCallback, useState, useEffect } from "react";
import { BreadcrumbsType } from "@/app/components/breadcrumbs/widget";
import { OpenMatchModelResponse, OpenMatchRequests, OpenMatchStatusEnum } from "@/app/api/open_match";

interface MatchesState {
  isLoading: boolean;
  error?: string;
  breadcrumbs: Array<BreadcrumbsType | string>;
  waitingMatches: OpenMatchModelResponse[];
  inProgressMatches: OpenMatchModelResponse[];
  canceledMatches: OpenMatchModelResponse[];
  completedMatches: OpenMatchModelResponse[];
  activeFilters: OpenMatchStatusEnum[];
}

const useMatches = () => {
  const [state, setState] = useState<MatchesState>({
    isLoading: true,
    error: undefined,
    breadcrumbs: [BreadcrumbsType.padel, BreadcrumbsType.matches],
    waitingMatches: [],
    inProgressMatches: [],
    canceledMatches: [],
    completedMatches: [],
    activeFilters: [
      OpenMatchStatusEnum.NOT_STARTED,
      OpenMatchStatusEnum.STARTED
    ],
  });

  const updateState = useCallback((updates: Partial<MatchesState>) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  const fetchMatchesByStatus = useCallback(async (status: OpenMatchStatusEnum) => {
    try {
      const matches = await OpenMatchRequests.getMatches({ status });
      const list = Array.isArray(matches) ? matches : [];
      
      switch (status) {
        case OpenMatchStatusEnum.NOT_STARTED:
          updateState({ waitingMatches: list });
          break;
        case OpenMatchStatusEnum.STARTED:
          updateState({ inProgressMatches: list });
          break;
        case OpenMatchStatusEnum.CANCELED:
          updateState({ canceledMatches: list });
          break;
        case OpenMatchStatusEnum.ARCHIVED:
          updateState({ completedMatches: list });
          break;
      }
      
      return list;
    } catch (error: any) {
      updateState({ error: error.message || "Failed to fetch matches" });
      return [];
    }
  }, [updateState]);

  const fetchAllMatches = useCallback(async () => {
    updateState({ isLoading: true, error: undefined });
    
    try {
      await Promise.all([
        fetchMatchesByStatus(OpenMatchStatusEnum.NOT_STARTED),
        fetchMatchesByStatus(OpenMatchStatusEnum.STARTED),
        fetchMatchesByStatus(OpenMatchStatusEnum.CANCELED),
        fetchMatchesByStatus(OpenMatchStatusEnum.ARCHIVED),
      ]);
    } catch (error: any) {
      updateState({ error: error.message || "Failed to fetch matches" });
    } finally {
      updateState({ isLoading: false });
    }
  }, [fetchMatchesByStatus, updateState]);

  const toggleFilters = useCallback((filters: OpenMatchStatusEnum[]) => {
    updateState({ activeFilters: filters });
  }, [updateState]);

  return {
    state,
    fetchAllMatches,
    fetchMatchesByStatus,
    toggleFilters,
  };
};

export default useMatches;